import styles from "./CallingLineList.module.scss";
import cn from "classnames";
import { BlockWithError, BlockWithInfo, BlockWithLabel, CustomTextField } from "@/ui";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ColumnMeta, ICallList, RowData } from "./CallingLineList.props";
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { StatusBar, StatusDiod, Table, TableFooter } from "@/components";
import { CLS } from "@/constants/accessRights";
import { formatDate } from "@/helpers";
import { ICall } from "@/types/clsTypes";
import { v4 as uuidv4 } from "uuid";
import H3 from "@/ui/H3/H3";
import { useAppSelector } from "@/store/hooks";
import { selectArchiveList } from "@/store/ses/sesArchiveSlice";
import { Box, Button, FormControl } from "@mui/material";
import { mapCallStatus } from "./CallingLineList.const";
import { useCallingList } from "./useCallingLineList";
import { Hint } from "@/HOC";
import { useElementWidth } from "@/hooks";
import AbonentsTableSettings from "./AbonentsTableSettings/AbonentsTableSettings";

const CallingList: FC<ICallList> = ({ tableMode, onRowClick, currentCall, setCallData }) => {
	const {
		callingListCash,
		callingList,
		isCallingListLoading,
		refetchCallingList,
		selectedRowId,
		selectedCallRowId,
		selectedRowData,
		selectedCallsList,
		selectedCallListId,
		clsHints,
		currentColumns,
		callsIdsBySearch,
		setCallsIdsBySearch,
		setCurrentColumns,
		isFiltersActive,
		startTime,
		endTime,
		decryptionSearchValue,
		handleSelectCallsOfDestination,
		handleSelectCall,
		handleFilterCalls,
		handleResetFilterCalls,
		setSelectedCallsList,
		setStartTime,
		setEndTime,
		setDecryptionSearchValue,
		isAccess,
		translate,
	} = useCallingList(/* tableMode, */ currentCall, onRowClick);

	const tableContainerRef = useRef<HTMLDivElement>(null);
	const tableContainerCallsListRef = useRef<HTMLDivElement>(null);
	const [selectedOptionalColumn, setSelectedOptionalColumn] = useState<string | null>(null);
	const [optionalColumnsList, setOptionalColumnsList] = useState<string[] | null>(null);
	const isOptionalColumnActive = !(selectedOptionalColumn?.length === 0 || selectedOptionalColumn !== "");

	const headerHints: Record<string, string> = {
		title_successAttempt: clsHints.hint_clsSuccessfulAttemptNumber,
		title_totalAttempts: clsHints.hint_clsTotalAttempts,
	};

	useEffect(() => {
		callsTable.setOptions((prev) => ({
			...prev,
			data: selectedCallsList?.calls ?? [],
		}));
	}, [JSON.stringify(selectedCallsList?.calls)]);

	const selectedDialogsList = useAppSelector(selectArchiveList);

	//колонки для основной таблицы
	const columns = useMemo<ColumnDef<RowData>[]>(() => {
		const seen = new Set<string>();

		const baseColumns: ColumnDef<RowData>[] = [
			{
				accessorKey: "destination",
				header: translate("title_abonent"),
				cell: (info) => info.row.original.destination,
				filterFn: "myCustomFilter",
				enableColumnFilter: true,
			},
		];

		const statusColumn: ColumnDef<RowData> = {
			id: "status",
			header: "",
			cell: ({ row }) => {
				const status = row.original.call_status as "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";

				const { color, titleStatus } = mapCallStatus({ status: status });

				return <StatusDiod color={color} text={translate(titleStatus)} pulsing={row.original.title_callStatus !== "COMPLETED"} diodParams={{ top: 8, left: 8 }} />;
			},
			enableSorting: false,
			enableColumnFilter: false,
			size: 50,
		};

		const optionalColumn: ColumnDef<RowData> | null = selectedOptionalColumn
			? {
					id: "selectedOptionalColumn",
					accessorKey: selectedOptionalColumn,
					header: translate(selectedOptionalColumn),
					cell: (info) => {
						// Безопасный доступ к свойству
						if ("metadata" in info.row.original) {
							const value = info.row.original.metadata[selectedOptionalColumn as keyof RowData];
							return value || "";
						}
						return "";
					},
					filterFn: "myCustomFilter",
					enableColumnFilter: true,
			  }
			: null;

		const uniqueColumns: ColumnDef<RowData>[] = currentColumns
			.filter((col) => {
				if (seen.has(col.key)) return false;
				seen.add(col.key);
				return true;
			})
			.map((col) => {
				const hint = headerHints[col.key];

				return {
					accessorKey: col.key,
					header: () =>
						hint ? (
							<Hint hint={hint} position={"top"} offset={[-8, 0]}>
								<span className={styles.headerEllipsis}>{translate(col.key)}</span>
							</Hint>
						) : (
							<span className={styles.headerEllipsis}>{translate(col.key)}</span>
						),
					cell: (info) => info.row.original[col.key] as string,
					filterFn: "myCustomFilter",
					enableColumnFilter: true,
				};
			});

		if (tableMode === "settingsMode") {
			return [...baseColumns, ...uniqueColumns];
		} else {
			const columnsArray = [statusColumn, ...baseColumns];
			if (optionalColumn && selectedOptionalColumn !== null && selectedOptionalColumn !== "") {
				columnsArray.push(optionalColumn);
			}
			columnsArray.push(...uniqueColumns);
			return columnsArray;
		}
	}, [currentColumns, tableMode, translate, callingListCash]);

	const columnCount = useMemo(() => {
		const uniqueKeys = new Set(currentColumns.map((col) => col.key));
		return 1 + uniqueKeys.size; // +1 за destination
	}, [currentColumns]);

	// useEffect(() => {
	// 	if (displayMode && displayMode.selectedCallList) {
	// 		setSelectedCallListId(displayMode.selectedCallList ?? null);
	// 		setSelectedRowId("none");
	// 		setSelectedCallRowId("none");
	// 		setSelectedCallsList(undefined);
	// 	}
	// }, [displayMode]);

	const tableData = useMemo(() => {
		if (callingList && callingList?.list) {
			if (!callingList?.list.items) return [];

			// превращаем metadata в массив { key, value }
			const metaColumns: ColumnMeta[] = callingList.list.items.flatMap((item) =>
				Object.entries(item.metadata).map(([key, value]) => ({
					key,
					value: String(value),
				}))
			);

			const observerColumns =
				callingListCash?.items?.flatMap(
					(item) =>
						item.status?.calls?.flatMap(() => {
							return [
								{
									key: "title_successAttempt",
									value: item.status?.success_attempt_number?.toString() ?? "",
								},
								{ key: "title_totalAttempts", value: item.status?.total_attempts.toString() ?? "" },
							];
						}) ?? [] // защита на случай, если calls нет
				) ?? [];

			if (tableMode === "observerMode") {
				setCurrentColumns(observerColumns ?? []);
			} else if (tableMode === "settingsMode") {
				setCurrentColumns(metaColumns);
			}

			// нормализуем список
			const normalizedList = callingList.list.items.map((el) => ({
				destination: el.destinations[0],
				...el.metadata,
			}));

			if (normalizedList[0]) {
				const optionalColumnsList = Object.keys(normalizedList[0]).filter((el) => el !== "destination");
				setOptionalColumnsList(optionalColumnsList);
			}

			let cashNormalizedList =
				callingListCash?.items?.flatMap((el) =>
					el.destinations.map((destination) => {
						const newId = uuidv4();
						const status = el.status ?? {}; // безопасно
						return {
							id: newId,
							destination,
							call_status: status.overall_status ?? "UNKNOWN", // дефолтное значение
							title_successAttempt: status.success_attempt_number ?? "-", // дефолтное число
							title_totalAttempts: status.total_attempts ?? 0,
							originalCalls: status.calls ?? [], // пустой массив, если нет звонков
							metadata: el.metadata,
						};
					})
				) ?? [];

			// оставляем только destinations, у которых есть звонки из поиска
			if ((decryptionSearchValue !== "" || startTime !== "" || endTime !== "") && isFiltersActive) {
				cashNormalizedList = cashNormalizedList.filter((row) => row.originalCalls?.some((c: ICall) => callsIdsBySearch.includes(c.uuid)));
				// иначе оставляем все записи
			}

			return tableMode === "observerMode" ? cashNormalizedList ?? [] : normalizedList ?? [];
		}
		return [];
	}, [
		callingList,
		selectedCallListId,
		isCallingListLoading,
		tableMode,
		callingListCash,
		callsIdsBySearch,
		decryptionSearchValue,
		startTime,
		endTime,
		selectedOptionalColumn,
	]);

	const table = useReactTable<any>({
		data: tableData,
		columns,
		initialState: {
			pagination: { pageIndex: 0, pageSize: 100 }, // начальное состояние
		},
		filterFns: {
			myCustomFilter: (row, columnId, filterValue) => {
				const value = row.getValue(columnId);
				if (value === undefined || value === null) return false;
				return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
			},
		},
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});
	////

	const callDetailsColumns = useMemo<ColumnDef<ICall>[]>(
		() => [
			{
				id: "status",
				header: "",
				cell: ({ row }) => {
					const status = row.original.call_status;

					const { color, titleStatus } = mapCallStatus({ status: status });

					return <StatusDiod color={color} text={translate(titleStatus)} pulsing={row.original.call_status !== "COMPLETED"} diodParams={{ top: 8, left: 8 }} />;
				},
				enableSorting: false,
				enableColumnFilter: false,
				size: 50,
			},
			{
				accessorKey: "call_time",
				header: translate("title_callTime"),
				cell: (info) => formatDate(info.row.original.call_time),
			},
			{
				accessorKey: "hangup_cause",
				header: translate("title_hangupCause"),
			},
		],
		[translate, JSON.stringify(callingListCash?.items), selectedCallsList?.calls, callingListCash]
	);

	const callsTableData = useMemo(() => {
		if (!callingList || !selectedRowData) return [];

		//  Если не нашли — пусто
		if (!selectedRowData) return [];

		const callsOfCurrentDestination = {
			calls:
				decryptionSearchValue !== "" && startTime !== "" && endTime !== "" && isFiltersActive
					? (selectedRowData.originalCalls || []).filter((c) => callsIdsBySearch.includes(c.uuid))
					: selectedRowData.originalCalls.map((c) => ({ ...c })),
			metadata: { ...selectedRowData.metadata },
		};
		//  Обновляем selectedCallsList
		setSelectedCallsList(callsOfCurrentDestination);

		return selectedRowId !== "none" ? callsOfCurrentDestination.calls : [];
	}, [selectedRowId, callingListCash, selectedRowData, callsIdsBySearch, decryptionSearchValue, startTime, endTime]);

	useEffect(() => {
		if (selectedDialogsList.data) {
			setCallsIdsBySearch(selectedDialogsList.data.map((el) => el.uuid));
		}
	}, [selectedDialogsList]);

	useEffect(() => {
		setCallData(undefined);
	}, [selectedRowId]);

	const statusCounters = useMemo(() => {
		const counters: Record<"PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "UNKNOWN", number> = { PENDING: 0, IN_PROGRESS: 0, COMPLETED: 0, FAILED: 0, UNKNOWN: 0 };

		tableData.forEach((row: any) => {
			const status = (row.call_status as "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED") ?? "UNKNOWN";
			counters[status] = (counters[status] ?? 0) + 1;
		});

		return counters;
	}, [tableData]);

	const callsTable = useReactTable<ICall>({
		data: callsTableData,
		columns: callDetailsColumns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		filterFns: {
			myCustomFilter: (row, columnId, filterValue) => {
				const value = row.getValue(columnId);
				if (!value) return false;
				return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
			},
		},
	});
	const [additionalOptionsContainerWidth, setAdditionalOptionsContainerWidth] = useState(0);
	const additionalOptionsContainer = useRef(null);
	const width = useElementWidth(additionalOptionsContainer);
	useEffect(() => {
		setAdditionalOptionsContainerWidth(width);
	}, [width]);

	return (
		<>
			<Hint hint={tableMode === "settingsMode" ? "" : clsHints.hint_clsCallList} position="right" spanStyle={{ width: "100%" }} offset={[0, -20]}>
				<section
					className={cn(styles["calling-line-calling-list-destinations"], {
						[styles["calling-line-calling-list-destinations-observer-mode"]]: tableMode === "observerMode",
					})}
				>
					<div className={cn(styles["calling-line-calling-list-destinations-container"])} ref={tableContainerRef}>
						{callingList?.error === 1 || callingList === undefined ? (
							<BlockWithError updateHandler={refetchCallingList} />
						) : (
							<>
								{tableMode === "observerMode" ? <H3 style={{ marginBottom: "12px" }}>{translate("title_abonentsList")}</H3> : <></>}
								{tableMode === "observerMode" && (
									<>
										<Hint hint={clsHints.hint_clsCallFilterBlock} position="right" offset={[0, -8]}>
											<BlockWithLabel label={translate("title_callsFilter")} sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
												<CustomTextField
													label={translate("input_textSearchInDialog")}
													value={decryptionSearchValue}
													onChange={(e) => setDecryptionSearchValue(e.target.value)}
												/>
												<Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
													<Box sx={{ flex: "1 1 200px", minWidth: 250 }}>
														<CustomTextField
															label={translate("input_start")}
															type="datetime-local"
															value={startTime}
															onChange={(e) => setStartTime(e.target.value)}
															sx={{ width: "100%" }}
														/>
													</Box>
													<Box sx={{ flex: "1 1 200px", minWidth: 250 }}>
														<CustomTextField
															label={translate("input_end")}
															type="datetime-local"
															value={endTime}
															onChange={(e) => setEndTime(e.target.value)}
															sx={{ width: "100%" }}
														/>
													</Box>
												</Box>
												<Box sx={{ display: "flex", flexDirection: "row", width: "100%", gap: "12px" }}>
													<Button sx={{ fontSize: 11, width: "100%" }} color="error" variant="outlined" onClick={handleResetFilterCalls}>
														{translate("button_reset")}
													</Button>
													<Button sx={{ fontSize: 11, width: "100%" }} onClick={handleFilterCalls} variant="outlined">
														{translate("button_search")}
													</Button>
												</Box>
											</BlockWithLabel>
										</Hint>
									</>
								)}

								<fieldset className={cn(styles["table-container"])} style={{ borderBottom: isAccess(CLS.LISTS_LIST) ? "none" : "1px solid #e0e0e0" }}>
									{isAccess(CLS.LISTS_LIST) ? (
										<Table<RowData>
											table={table}
											tableContainerRef={tableContainerRef}
											isRowHoverEnebled={tableMode === "observerMode"}
											withFilters={true}
											completeHeaderOptions={{}}
											onRowClick={tableMode === "observerMode" ? handleSelectCallsOfDestination : undefined}
											selectedRowId={selectedRowId}
											headerPadding="0 0 8px 6px"
											columnsWidth={
												<colgroup>
													{tableMode === "observerMode" ? (
														<>
															<col
																style={{
																	width: tableData.length === 0 ? "0" : !isOptionalColumnActive ? "10%" : "5%",
																}}
															></col>
															<col
																style={{
																	width: tableData.length === 0 ? "100%" : !isOptionalColumnActive ? "31%" : "20%",
																}}
															></col>
															<col
																style={{
																	width: tableData.length === 0 ? "0" : !isOptionalColumnActive ? "31%" : "20%",
																}}
															></col>
															<col
																style={{
																	width: tableData.length === 0 ? "0" : !isOptionalColumnActive ? "31%" : "20%",
																}}
															></col>
															{!isOptionalColumnActive ? <></> : <col style={{ width: tableData.length === 0 ? "0" : "20%" }}></col>}
														</>
													) : (
														Array.from({ length: columnCount }, (_, i) => <col key={i} style={{ width: `${100 / columnCount}%` }} />)
													)}
												</colgroup>
											}
										/>
									) : (
										<BlockWithInfo title={translate("title_noAccessRights")} />
									)}
								</fieldset>
								{isAccess(CLS.LISTS_LIST) && (
									<TableFooter<any>
										additionalContent={
											tableMode === "observerMode" ? (
												<FormControl ref={additionalOptionsContainer} sx={{ width: "100%", alignItems: "center", display: "flex", flexDirection: "row", gap: "16px" }}>
													<AbonentsTableSettings
														selectedOptionalColumn={selectedOptionalColumn}
														setSelectedOptionalColumn={setSelectedOptionalColumn}
														optionalColumnsList={optionalColumnsList}
														parentBlockWidth={additionalOptionsContainerWidth}
														hint={clsHints.hint_clsOptionalColumn}
													/>
													<StatusBar parentWidth={additionalOptionsContainerWidth} statuses={statusCounters} />
												</FormControl>
											) : (
												<></>
											)
										}
										table={table}
										withExpand={false}
										tableContainerRef={tableContainerRef}
									/>
								)}
							</>
						)}
					</div>
				</section>
			</Hint>

			{tableMode === "observerMode" && (
				<Hint hint={clsHints.hint_clsCallHistory} position="left" spanStyle={{ width: "100%" }} offset={[0, -20]}>
					<section className={cn(styles["calling-line-calling-list-calls"])}>
						<div className={cn(styles["calling-line-calling-list-calls-container"])} ref={tableContainerCallsListRef}>
							<div className={cn(styles["calling-line-calling-list-calls-content"])} ref={tableContainerCallsListRef}>
								<H3 style={{ marginBottom: "12px" }}>{translate("title_abonentCallHistory")}</H3>
								<div className={cn(styles["table-container"])}>
									{selectedCallsList ? (
										<Table<ICall>
											table={callsTable}
											tableContainerRef={tableContainerCallsListRef}
											isRowHoverEnebled={true}
											withFilters={true}
											onRowClick={handleSelectCall}
											completeHeaderOptions={{}}
											selectedRowId={selectedCallRowId}
											headerPadding="0 0 8px 6px"
											columnsWidth={
												<colgroup>
													<col style={{ width: `5%` }} />
													<col style={{ width: `42%` }} />
													<col style={{ width: `53%` }} />
												</colgroup>
											}
										/>
									) : (
										<BlockWithInfo title={translate("title_selectDestination")} />
									)}
								</div>
								<TableFooter<ICall> table={callsTable} tableContainerRef={tableContainerCallsListRef} withExpand={false} />
							</div>
						</div>
					</section>
				</Hint>
			)}
		</>
	);
};

export default CallingList;
